import React, { useEffect, lazy, Suspense, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from "react-router-dom";
import ThemeWrapper from "./ThemeWrapper";
import AuthRoute from "./components/Auth/AuthRoute";
import { AuthStatusProvider } from "./components/Auth/AuthStatusContext";
import Layout from "./components/Common/Layout";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { setGlobalToast } from "./services/ApiClient";
import { setGlobalAuthTokenGetter, debugAuthTokens } from "./utils/authUtils";
import { useAuth } from "react-oidc-context";
import { CircularProgress, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";

// Lazy load route components for code splitting
const DashboardPanel = lazy(() => import("./Home/DashboardPanel"));
const CampaignDetails = lazy(() => import("./Home/CampaignDetails"));
const ApplicationsForm = lazy(() => import("./components/Applications/ApplicationsForm"));
const DISCPage = lazy(() => import("./components/Applications/DISCPage"));
const LocationPage = lazy(() => import("./components/Applications/LocationPage"));
const AvailabilityPage = lazy(() => import("./components/Applications/AvailabilityPage"));
const AuthCallback = lazy(() => import("./components/Auth/AuthCallback"));
const ConfirmAccount = lazy(() => import("./components/Auth/ConfirmAccount"));
const Unauthorized = lazy(() => import("./components/Auth/Unauthorized"));
const SignOut = lazy(() => import("./components/Auth/SignOut"));
const ProgramRoutes = lazy(() => import("./components/Application/ProgramRoutes"));
const SignInWrapper = lazy(() => import("./components/Auth/SignInWrapper"));
const SignUpWrapper = lazy(() => import("./components/Auth/SignUpWrapper"));
const ForgotPasswordWrapper = lazy(() => import("./components/Auth/ForgotPasswordWrapper"));
const NotFoundRedirect = lazy(() => import("./components/Common/NotFoundRedirect"));

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
);

// Component to set up global toast function
const ToastSetup: React.FC = () => {
  const { showToast } = useToast();

  useEffect(() => {
    setGlobalToast(showToast);
  }, [showToast]);

  return null;
};

// Component to set up global auth token getter
const AuthSetup: React.FC = () => {
  const auth = useAuth();

  useEffect(() => {
    const getAccessToken = () => {
      // Debug auth tokens on first load
      const isDev = (import.meta as any).env?.MODE === 'development' || (import.meta as any).env?.DEV === true;
      if (isDev) {
        debugAuthTokens();
      }

      // Try to get access token from auth.user first
      if (auth.user?.access_token) {
        return auth.user.access_token;
      }

      console.log('⚠️ No access token in auth.user, auth state:', {
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        hasUser: !!auth.user,
        userKeys: auth.user ? Object.keys(auth.user) : []
      });

      return null;
    };

    setGlobalAuthTokenGetter(getAccessToken);
  }, [auth]);

  return null;
};

const CampaignRedirect: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    const fetchCampaignId = async () => {
      if (!code) {
        setShowErrorModal(true);
        setLoading(false);
        return;
      }

      try {
        // Import ApplyService dynamically to avoid circular dependencies
        const { default: ApplyService } = await import('./services/ApplyService');
        const applyService = new ApplyService();
        const campaignId = await applyService.getCampaignIdFromCode(code);
        navigate(`/campaign/${campaignId}`, { replace: true });
      } catch (error) {
        console.error('Error fetching campaign ID:', error);
        setShowErrorModal(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignId();
  }, [code, navigate]);

  const handleModalClose = () => {
    setShowErrorModal(false);
    navigate('/dashboard');
  };

  if (loading) {
    return <LoadingFallback />;
  }

  return (
    <Dialog open={showErrorModal} onClose={handleModalClose}>
      <DialogTitle>Invalid Campaign Code</DialogTitle>
      <DialogContent>
        <Typography>
          The application code you entered is incorrect. Please contact the organizer of your mentorship program to get the correct code.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleModalClose} variant="contained" color="primary">
          Go to Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    // Application startup - logging removed
  }, []);

  return (
    <ErrorBoundary>
      <ThemeWrapper>
        <ToastProvider>
          <ToastSetup />
          <AuthSetup />
          <Router basename={(import.meta as any).env?.BASE_URL || '/'} future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <AuthStatusProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="/oauth2/idpresponse" element={<AuthCallback />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/confirm-account" element={<ConfirmAccount />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                  <Route path="/signout" element={<SignOut />} />

                  {/* Cognito wrapper pages */}
                  <Route path="/signin" element={<SignInWrapper />} />
                  <Route path="/signup" element={<SignUpWrapper />} />
                  <Route path="/forgot-password" element={<ForgotPasswordWrapper />} />

                  {/* All protected routes wrapped in a single AuthRoute */}
                  <Route element={<AuthRoute />}>
                    {/* Dashboard routes */}
                    <Route index element={<Layout><DashboardPanel /></Layout>} />
                    <Route path="/dashboard" element={<Layout><DashboardPanel /></Layout>} />
                    <Route path="/campaign/:id" element={<Layout><CampaignDetails /></Layout>} />

                    {/* DISC Assessment Page */}
                    <Route path="/disc" element={<Layout><DISCPage /></Layout>} />

                    {/* Location Page */}
                    <Route path="/location" element={<Layout><LocationPage /></Layout>} />

                    {/* Availability Page */}
                    <Route path="/availability" element={<Layout><AvailabilityPage /></Layout>} />

                    {/* New Applications Form */}
                    <Route path="/applications/:id" element={<Layout fullScreen><ApplicationsForm /></Layout>} />

                    {/* Program routes with persistent navigation - All point to ProgramRoutes */}
                    <Route path="/apply/:code" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code/apply" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code/match-review" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code/mentorship-materials" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code/check-in" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code/help" element={<Layout fullScreen><ProgramRoutes /></Layout>} />
                    <Route path="/:code" element={<CampaignRedirect />} />
                  </Route>

                  {/* Catch-all route for unknown paths - authentication-aware redirect */}
                  <Route path="*" element={<NotFoundRedirect />} />
                </Routes>
              </Suspense>
            </AuthStatusProvider>
          </Router>
        </ToastProvider>
      </ThemeWrapper>
    </ErrorBoundary>
  );
};

export default App;
